(() => {
  if (window.BHSearch) { window.BHSearch.mountPages(); return; }
  let enginePromise;
  let core;
  const plainText = (value) => {
    const decoder = document.createElement('textarea');
    decoder.innerHTML = String(value || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return decoder.value;
  };
  const config = () => {
    const cfg = JSON.parse(document.getElementById('BhSearchConfig').textContent);
    cfg.labels = Object.fromEntries(Object.entries(cfg.labels).map(([key, value]) => [key, plainText(value)]));
    return cfg;
  };
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const safeUrl = (value, image = false) => {
    try {
      const url = new URL(value, location.origin);
      if (!['https:', 'http:'].includes(url.protocol)) return '';
      if (!image && url.origin !== location.origin) return '';
      return image ? url.href : url.pathname + url.search + url.hash;
    } catch { return ''; }
  };
  const keyFor = (url, cfg) => {
    const path = new URL(url, location.origin).pathname;
    const prefix = cfg.root.replace(/\/$/, '');
    return (prefix && path.startsWith(prefix + '/') ? path.slice(prefix.length) : path) + new URL(url, location.origin).hash;
  };
  const readJSON = async (url) => {
    const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(20000) });
    if (!response.ok) throw new Error('Search data unavailable');
    return response.json();
  };
  const readCatalog = async (cfg) => {
    const initial = new URL(cfg.search, location.origin);
    initial.searchParams.set('view', 'bh-catalog');
    const queue = [initial.href];
    const visited = new Set();
    const documents = new Map();
    while (queue.length) {
      const url = queue.shift();
      if (visited.has(url)) continue;
      visited.add(url);
      const chunk = await readJSON(url);
      if (!Array.isArray(chunk.documents)) throw new Error('Invalid search catalog');
      chunk.documents.filter(Boolean).forEach((entry) => {
        if (safeUrl(entry.url)) {
          for (const field of ['title', 'text', 'meta', 'price']) {
            if (typeof entry[field] === 'string') entry[field] = plainText(entry[field]);
          }
          documents.set(entry.url, entry);
        }
      });
      (chunk.next || []).filter(Boolean).forEach((next) => {
        const target = new URL(next, initial);
        if (target.origin !== initial.origin || target.pathname !== initial.pathname) return;
        target.searchParams.set('view', 'bh-catalog');
        if (!visited.has(target.href)) queue.push(target.href);
      });
    }
    return [...documents.values()];
  };
  const loadEngine = () => {
    if (enginePromise) return enginePromise;
    const cfg = config();
    const coreURL = cfg.core;
    const readSnapshot = async () => {
      const manifest = await readJSON(cfg.index);
      const parts = await Promise.all(manifest.parts.map((part) => {
        const url = new URL(part, new URL(cfg.index, location.href));
        url.searchParams.set('bh_revision', manifest.capturedAt);
        return readJSON(url);
      }));
      return { ...manifest, documents: parts.flatMap((part) => part.documents) };
    };
    enginePromise = Promise.all([import(cfg.library), import(coreURL), readSnapshot(), readCatalog(cfg)])
      .then(([library, module, snapshot, live]) => {
        core = module;
        const captured = new Map((cfg.locale.startsWith(snapshot.locale) ? snapshot.documents : []).map((entry) => [keyFor(entry.url, cfg), entry]));
        const documents = live.map((entry) => {
          const saved = captured.get(keyFor(entry.url, cfg));
          return { ...saved, ...entry, image: entry.image || saved?.image || '', text: [entry.text, saved?.text].filter(Boolean).join(' ') };
        });
        return core.buildSearch(library.default, documents);
      }).catch((error) => { enginePromise = null; throw error; });
    return enginePromise;
  };
  const groupFor = (entry) => ['page', 'article', 'policy'].includes(entry.type) ? 'knowledge' : entry.type;
  const groupsFor = (entries) => {
    const groups = new Map();
    entries.forEach((entry) => {
      const key = groupFor(entry);
      if (!groups.has(key)) groups.set(key, { key, entries: [] });
      groups.get(key).entries.push(entry);
    });
    return [...groups.values()];
  };
  const arrow = () => document.getElementById('BhSearchArrow')?.content.cloneNode(true) || document.createTextNode('');
  const imageFor = (entry, className = '') => {
    const url = entry.image && safeUrl(entry.image, true);
    if (!url) return null;
    const image = element('img', className);
    image.src = url;
    image.alt = '';
    image.width = 640;
    image.height = 480;
    image.loading = 'lazy';
    image.addEventListener('error', () => { if (image.parentElement?.classList.contains('bh-search-card__media')) image.parentElement.remove(); else image.remove(); }, { once: true });
    return image;
  };
  const cardFor = (entry, query, cfg, featured = false) => {
    const card = element('article', `bh-search-card bh-search-card--${entry.type}${featured ? ' bh-search-card--featured' : ''}`);
    card.dataset.resultUrl = entry.url;
    const link = element('a');
    link.href = safeUrl(entry.url);
    const image = !['lexicon', 'policy'].includes(entry.type) && imageFor(entry);
    if (image) { const media = element('div', 'bh-search-card__media'); media.append(image); link.append(media); }
    const body = element('div', 'bh-search-card__body');
    if (featured) body.append(element('span', 'bh-search-card__type', cfg.labels.featured));
    body.append(element(featured ? 'h2' : 'h3', '', entry.title));
    const description = core.excerpt(entry.text, query, entry.type === 'lexicon' ? 140 : 175);
    if (description) body.append(element('p', 'bh-search-card__excerpt', description));
    if (entry.type === 'product') {
      const price = element('div', 'bh-search-card__price', entry.price);
      if (entry.available === false) price.append(element('span', 'bh-search-card__stock', cfg.labels.sold_out));
      body.append(price);
    }
    const direction = element('span', 'bh-search-card__arrow');
    direction.setAttribute('aria-hidden', 'true'); direction.append(arrow());
    link.append(body, direction); card.append(link);
    return card;
  };
  const initPage = (root) => {
    if (root.dataset.searchMounted) return;
    root.dataset.searchMounted = 'true';
    const cfg = config();
    const form = root.querySelector('form');
    const input = form.querySelector('[name="q"]');
    const tabs = [...root.querySelectorAll('[data-search-tab]')];
    const status = root.querySelector('[data-search-status]');
    const panel = root.querySelector('[role="tabpanel"]');
    const resultsBox = root.querySelector('[data-search-results]');
    const sort = root.querySelector('[data-search-sort]');
    const more = root.querySelector('[data-search-more]');
    const empty = root.querySelector('[data-search-empty]');
    const notice = root.querySelector('[data-search-notice]');
    const fallback = root.querySelector('[data-search-fallback]');
    let results = [];
    let active = 'all';
    let limit = 24;
    let generation = 0;
    let query = input.value.trim();
    let inputTimer;
    let loading = false;
    const selectGroup = (key) => {
      active = key; limit = 24; sort.value = 'relevance'; render();
    };
    const render = () => {
      const filtered = results.filter((entry) => active === 'all' || groupFor(entry) === active);
      if (sort.value === 'title') filtered.sort((a, b) => a.title.localeCompare(b.title, cfg.locale));
      if (sort.value.startsWith('price-')) filtered.sort((a, b) => sort.value === 'price-up' ? a.priceValue - b.priceValue : b.priceValue - a.priceValue);
      tabs.forEach((tab) => {
        const key = tab.dataset.searchTab;
        const selected = key === active;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        tab.querySelector('[data-tab-count]').textContent = results.filter((entry) => key === 'all' || groupFor(entry) === key).length;
      });
      panel.setAttribute('aria-labelledby', tabs.find((tab) => tab.dataset.searchTab === active).id);
      resultsBox.replaceChildren();
      const featured = active === 'all' && sort.value === 'relevance' && filtered[0]?.tier >= 3 ? filtered[0] : null;
      if (featured) resultsBox.append(cardFor(featured, query, cfg, true));
      const groups = groupsFor(active === 'all' ? filtered : filtered.slice(0, limit));
      groups.forEach(({ key, entries }) => {
        const remaining = entries.filter((entry) => entry !== featured);
        if (!remaining.length) return;
        const previewLimit = { product: 4, collection: 4, knowledge: 3, lexicon: 4 }[key];
        const shown = active === 'all' ? remaining.slice(0, previewLimit) : remaining;
        const section = element('section', `bh-search-group bh-search-group--${key}`);
        const heading = element('div', 'bh-search-group__heading');
        const title = element('h2', '', cfg.labels[`group_${key}`]);
        title.append(element('span', 'bh-search-group__count', results.filter((entry) => groupFor(entry) === key).length));
        heading.append(title);
        if (active === 'all') {
          const all = element('button', 'bh-search-group__all', cfg.labels.group_all);
          all.type = 'button'; all.setAttribute('aria-label', `${cfg.labels.group_all}: ${cfg.labels[`group_${key}`]}`);
          all.append(arrow());
          all.addEventListener('click', () => { selectGroup(key); tabs.find((tab) => tab.dataset.searchTab === key)?.focus(); });
          heading.append(all);
        }
        const list = element('div', 'bh-search-group__list');
        list.append(...shown.map((entry) => cardFor(entry, query, cfg)));
        section.append(heading, list); resultsBox.append(section);
      });
      status.textContent = cfg.labels.count.replace('[COUNT]', filtered.length).replace('[QUERY]', query);
      empty.hidden = filtered.length > 0 || !query;
      more.hidden = active === 'all' || filtered.length <= limit;
      sort.querySelectorAll('[data-product-sort]').forEach((option) => { option.hidden = active !== 'product'; option.disabled = active !== 'product'; });
    };
    const run = async (value) => {
      query = value.trim().slice(0, 240);
      const current = ++generation;
      limit = 24;
      fallback.hidden = true;
      empty.hidden = true;
      more.hidden = true;
      notice.hidden = true;
      resultsBox.replaceChildren();
      results = [];
      if (!query) {
        loading = false; sort.disabled = false; tabs.forEach((tab) => { tab.disabled = false; });
        render(); panel.setAttribute('aria-busy', 'false'); status.textContent = ''; return;
      }
      loading = true;
      sort.disabled = true;
      tabs.forEach((tab) => { tab.disabled = true; });
      panel.setAttribute('aria-busy', 'true');
      status.textContent = cfg.labels.loading;
      try {
        const engine = await loadEngine();
        if (current !== generation) return;
        const response = engine.query(query);
        results = response.results;
        notice.hidden = !response.fuzzy;
        notice.textContent = cfg.labels.corrected;
        render();
      } catch {
        if (current !== generation) return;
        status.textContent = cfg.labels.error;
        const retry = element('button', 'bh-search__more', cfg.labels.retry);
        retry.type = 'button'; retry.addEventListener('click', () => run(input.value));
        resultsBox.replaceChildren(retry);
        fallback.hidden = false;
      } finally {
        if (current === generation) {
          loading = false; sort.disabled = false;
          tabs.forEach((tab) => { tab.disabled = false; });
          panel.setAttribute('aria-busy', 'false');
        }
      }
    };
    const updateURL = (push) => {
      const url = new URL(location.href);
      url.searchParams.set('q', input.value.trim());
      url.searchParams.delete('page');
      (push ? history.pushState : history.replaceState).call(history, {}, '', url);
    };
    form.addEventListener('submit', (event) => { event.preventDefault(); clearTimeout(inputTimer); updateURL(true); run(input.value); });
    input.addEventListener('input', () => { clearTimeout(inputTimer); inputTimer = setTimeout(() => { updateURL(false); run(input.value); }, 220); });
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => { if (!loading) selectGroup(tab.dataset.searchTab); });
      tab.addEventListener('keydown', (event) => {
        const next = { ArrowRight: (index + 1) % tabs.length, ArrowLeft: (index - 1 + tabs.length) % tabs.length, Home: 0, End: tabs.length - 1 }[event.key];
        if (next !== undefined) { event.preventDefault(); tabs[next].click(); tabs[next].focus(); }
      });
    });
    sort.addEventListener('change', render);
    more.addEventListener('click', () => { const firstNew = limit; limit += 24; render(); resultsBox.querySelectorAll('.bh-search-card > a')[firstNew]?.focus(); });
    window.addEventListener('popstate', () => { input.value = new URL(location.href).searchParams.get('q') || ''; run(input.value); });
    run(query);
  };
  const initHeader = (root) => {
    const cfg = config();
    const input = root.querySelector('.main-search__input');
    const box = root.querySelector('.main-search__results');
    box.classList.add('bh-search-suggestions');
    let timer;
    let generation = 0;
    let resultId = 0;
    const reset = () => {
      root.activeResultIndex = -1;
      input.removeAttribute('aria-activedescendant');
      root.classList.remove('main-search--loading', 'main-search--has-results', 'main-search--no-results');
    };
    const search = async () => {
      const query = input.value.trim().slice(0, 240);
      const current = ++generation;
      reset(); box.replaceChildren(); input.setAttribute('aria-expanded', 'false');
      if (!query) return;
      root.classList.add('main-search--loading');
      box.append(element('p', '', cfg.labels.loading));
      try {
        const engine = await loadEngine();
        if (current !== generation || input.value.trim().slice(0, 240) !== query) return;
        const found = engine.query(query).results;
        box.replaceChildren();
        const wrapper = element('div', 'bh-quick-wrap');
        const quickResult = (entry, featured = false) => {
          const option = element('div', 'bh-quick-result');
          if (featured) option.classList.add('bh-quick-result--featured');
          option.setAttribute('role', 'option'); option.setAttribute('aria-selected', 'false');
          option.id = `BhQuickResult-${++resultId}`;
          const link = element('a'); link.href = safeUrl(entry.url);
          const image = entry.type === 'product' && imageFor(entry);
          if (image) link.append(image);
          const body = element('span', 'bh-quick-result__text');
          if (featured) body.append(element('span', 'bh-quick-result__type', cfg.labels.featured));
          body.append(element('span', 'bh-quick-result__title', entry.title));
          if (entry.type === 'product') body.append(element('span', 'bh-quick-result__price', entry.price));
          else if (featured && entry.text) body.append(element('span', 'bh-quick-result__excerpt', core.excerpt(entry.text, query, 100)));
          const direction = element('span', 'bh-quick-result__arrow');
          direction.setAttribute('aria-hidden', 'true'); direction.append(arrow());
          link.append(body, direction); option.append(link); return option;
        };
        const featured = found[0];
        if (featured) wrapper.append(quickResult(featured, true));
        const list = element('div', 'bh-quick-list');
        groupsFor(found).forEach(({ key, entries }) => {
          const remaining = entries.filter((entry) => entry !== featured);
          if (!remaining.length) return;
          const group = element('div', `bh-quick-group bh-quick-group--${key}`);
          group.setAttribute('role', 'group'); group.setAttribute('aria-label', cfg.labels[`group_${key}`]);
          const heading = element('div', 'bh-quick-group__heading', cfg.labels[`group_${key}`]);
          heading.append(element('span', 'bh-quick-group__count', entries.length));
          group.append(heading, ...remaining.slice(0, 2).map((entry) => quickResult(entry)));
          list.append(group);
        });
        wrapper.append(list);
        if (found.length) { box.append(wrapper); root.classList.add('main-search--has-results'); input.setAttribute('aria-expanded', 'true'); }
        else { box.append(element('p', '', cfg.labels.empty)); root.classList.add('main-search--no-results'); }
        const all = element('a', 'bh-quick-all btn btn--primary', found.length ? cfg.labels.view_count.replace('[COUNT]', found.length) : cfg.labels.view_all);
        all.append(arrow());
        const url = new URL(cfg.search, location.origin); url.searchParams.set('q', query); all.href = url.pathname + url.search;
        const footer = element('div', 'bh-quick-footer'); footer.append(all); box.append(footer);
        const status = root.querySelector('#HeaderSearchResultsStatus');
        if (status) status.textContent = cfg.labels.count.replace('[COUNT]', found.length).replace('[QUERY]', query);
      } catch {
        if (current !== generation) return;
        root.classList.add('main-search--no-results');
        box.replaceChildren(element('p', '', cfg.labels.error));
        const link = element('a', 'bh-quick-all btn btn--primary', cfg.labels.view_all);
        const url = new URL(cfg.search, location.origin); url.searchParams.set('q', query); link.href = url.pathname + url.search; box.append(link);
      } finally { if (current === generation) root.classList.remove('main-search--loading'); }
    };
    input.addEventListener('input', () => { clearTimeout(timer); ++generation; reset(); box.replaceChildren(); input.setAttribute('aria-expanded', 'false'); timer = setTimeout(search, 220); });
    input.addEventListener('focus', () => { if (input.value.trim() && !box.children.length) search(); });
  };
  const mountPages = () => document.querySelectorAll('[data-bh-search]').forEach(initPage);
  window.BHSearch = { initHeader, mountPages, loadEngine };
  mountPages();
  document.addEventListener('shopify:section:load', mountPages);
})();
